import {
  initObservability,
  getProvider,
  analyticsReady,
  resolveAnalyticsSelection,
  resolveTracingOrigins,
  compositeAnalytics,
  analyticsEvidence,
  analyticsEvidenceEnabled,
  analyticsSyntheticTrafficEnabled,
  templatizeRumViewName,
} from '@galapagos/observability';
import {
  createPostHogLegacyMirrorProvider,
  createPostHogWebProvider,
} from '@galapagos/observability/posthog/web';
import { createWebRumAnalytics, startWebRumView } from '@galapagos/observability/web-rum';
import {
  PRODUCT_ANALYTICS_CONTRACT_VERSION,
  analyticsContextSchema,
  buildContextFeatureFlagEvaluationEvents,
  buildViewEvent,
  buildActionEvent,
  captureProductEvent,
  normalizeAnalyticsEnvironment,
  type AnalyticsContext,
  type ProductEvent,
} from '@galapagos/product-analytics';
import { currentBookingIncentive } from '@/services/link-incentive';

/** Canonical web navigation event. */
export const PAGE_VIEW_EVENT = 'screen_viewed';

function parseSampleRate(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Initialise observability for the web app. Two independent axes compose on the
 * one seam:
 *  - The `web` logs/metrics/traces bundle + Datadog RUM, enabled when both a RUM
 *    `applicationId` and client token are present (the public-by-design
 *    `NEXT_PUBLIC_DATADOG_*` env). Absent (e.g. before the RUM app is minted) →
 *    RUM/logs stay off and the app still renders (fail-safe).
 *  - PostHog product analytics, opt-in via `NEXT_PUBLIC_ANALYTICS_PROVIDER`
 *    (unchanged). When both are active a composite fans `setUser`/`track` to
 *    PostHog AND Datadog RUM, so the server-minted analytics subject reaches
 *    both providers. Safe to call once at the app root.
 */
export async function initWebAnalytics(): Promise<void> {
  const selection = resolveAnalyticsSelection(process.env.NEXT_PUBLIC_ANALYTICS_PROVIDER);
  const analyticsEnvironment =
    process.env.NEXT_PUBLIC_DATADOG_ENV ?? process.env.NODE_ENV ?? 'development';
  const replayOverride = process.env.NEXT_PUBLIC_POSTHOG_SESSION_REPLAY;
  const evidenceEnabled = analyticsEvidenceEnabled(
    process.env.NEXT_PUBLIC_ANALYTICS_EVIDENCE,
    process.env.NEXT_PUBLIC_DATADOG_ENV,
  );
  const posthog =
    selection === 'posthog'
      ? createPostHogWebProvider({
          key: process.env.NEXT_PUBLIC_POSTHOG_KEY,
          host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
          environment: analyticsEnvironment,
          sessionReplaySampleRate: parseSampleRate(
            process.env.NEXT_PUBLIC_POSTHOG_SESSION_REPLAY_SAMPLE_RATE,
          ),
          captureSyntheticTraffic: analyticsSyntheticTrafficEnabled(
            process.env.NEXT_PUBLIC_ANALYTICS_EVIDENCE,
            process.env.NEXT_PUBLIC_DATADOG_ENV ?? process.env.NODE_ENV,
          ),
          // Client exceptions reach PostHog with a stack; the provider
          // credential-scrubs `$exception` payloads instead of chart-sanitizing them.
          captureExceptions: true,
          sessionReplay:
            replayOverride === undefined
              ? ['dev', 'development'].includes(analyticsEnvironment.toLowerCase())
              : replayOverride.trim().toLowerCase() === 'true',
        })
      : undefined;
  const evidence = evidenceEnabled ? analyticsEvidence.provider : undefined;
  // The legacy project the growth landing pages report to, so a landing-page
  // visitor and their signup stay one person there (ADR-167). Independent of the
  // primary selection; the mirror forwards only the legacy-parity events and is
  // inert unless both values are set.
  const legacyKey = process.env.NEXT_PUBLIC_POSTHOG_LEGACY_KEY?.trim();
  const legacyHost = process.env.NEXT_PUBLIC_POSTHOG_LEGACY_HOST?.trim();
  const legacyMirror =
    legacyKey && legacyHost
      ? createPostHogLegacyMirrorProvider({ key: legacyKey, host: legacyHost })
      : undefined;
  const productSinks = [posthog, evidence, legacyMirror].filter((item) => item !== undefined);
  const productAnalytics =
    productSinks.length > 1 ? compositeAnalytics(productSinks) : productSinks[0];

  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;

  // RUM off (tokens unminted): preserve the analytics-only behaviour.
  if (!clientToken || !applicationId) {
    await initObservability({ analytics: productAnalytics });
    return;
  }

  const config = {
    clientToken,
    applicationId,
    site: process.env.NEXT_PUBLIC_DATADOG_SITE,
    service: 'galapagos-web',
    env: process.env.NEXT_PUBLIC_DATADOG_ENV ?? 'development',
    version: process.env.NEXT_PUBLIC_APP_VERSION ?? '0.0.0',
    sessionSampleRate: parseSampleRate(process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE),
    allowedTracingUrls: resolveTracingOrigins(process.env.NEXT_PUBLIC_API_URL),
    // Page templates so `view.url` / `view.referrer` reach Datadog as templates
    // rather than interpolated paths (ADR-069 Rule 1). The RUM SDK fills those
    // from `window.location`, so no call site controls them. This app's routes
    // carry no identifier today, so injecting the set protects view-URL grouping
    // rather than privacy — without it those URLs would be destroyed.
    routeTemplates: { page: [...WEB_PAGE_ROUTE_TEMPLATES] },
    interactionWithheldViews: SIGNUP_INTERACTION_WITHHELD_VIEWS,
  };

  await initObservability({
    provider: 'web',
    config,
    // When PostHog is active, fan the analytics seam to BOTH sinks so usr.id
    // reaches Datadog RUM without dropping PostHog; otherwise the base `web`
    // provider's own RUM analytics handles it.
    analytics: productAnalytics
      ? compositeAnalytics([productAnalytics, createWebRumAnalytics(config)])
      : undefined,
  });
}

/**
 * Signup's health-question pages. On these, Datadog RUM keeps the page view and
 * its errors and drops clicks, network calls and long tasks: how many answers a
 * member saved, or a control only some programs show, would tell Datadog which
 * substance they are working on even though no page or event names it. PostHog,
 * which may hold health information, still gets every product event.
 */
export const SIGNUP_INTERACTION_WITHHELD_VIEWS = [
  '/quiz-motivation',
  '/quiz-goal',
  '/baseline-week',
  '/baseline-typical',
  '/baseline-adjust',
  '/screening',
] as const;

/**
 * The rebuilt signup flow's pages, served at the site root: one static page per
 * step, the off-path pages and the legal pages. The entry is `/` itself.
 */
const SIGNUP_PAGE_ROUTE_TEMPLATES = [
  '/context',
  '/substance',
  '/legal-name',
  '/account',
  '/code',
  '/plan-details',
  '/eligibility-check',
  '/employer-search',
  '/address',
  '/birth-date',
  '/contact',
  '/quiz-motivation',
  '/quiz-goal',
  '/baseline-week',
  '/baseline-typical',
  '/baseline-adjust',
  '/screening',
  '/wrap-up',
  '/hdhp-notice',
  '/hdhp-booking',
  '/rewards-intro',
  '/rewards-points',
  '/call-choice',
  '/language-preference',
  '/booking',
  '/booking-unavailable',
  '/download',
  '/under-age',
  '/minor-unsupported',
  '/registered',
  '/error',
  '/book-a-call',
  '/login',
  '/login-code',
  '/login-no-progress',
  '/sso-contact',
  '/sso-code',
  '/terms-of-use',
  '/privacy-policy',
  '/notice-of-privacy-practices',
  '/consumer-health-data-privacy-notice',
  '/ai-notice',
  '/tcpa-authorization',
] as const;

/**
 * The canonical web page route templates, mirroring what
 * {@link canonicalWebRouteTemplate} recognises. Kept as data so the provider can
 * be given the set; the function itself is deliberately left alone, because its
 * output feeds product-analytics `route_template` and `screen_id`. A test asserts
 * the two stay in agreement.
 *
 * `/signup` is listed although it is redirect-only and has no canonical
 * template: RUM fills `view.url` from `window.location`, so the old address
 * still needs a template for the moment before the redirect lands.
 */
export const WEB_PAGE_ROUTE_TEMPLATES = [
  '/',
  '/saml',
  '/component-gallery',
  '/signup',
  ...SIGNUP_PAGE_ROUTE_TEMPLATES,
] as const;

/**
 * The pathname alone, without query or fragment, and without a trailing slash
 * (the static export serves every route as `/route/`), so `/login/` and
 * `/login` resolve to the same template.
 */
function pathnameOf(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname;
}

/** Bounded warn for a route outside the registry — the templatized route carries no identifiers. */
function logUnknownWebRoute(path: string): void {
  getProvider().logs.log('warn', 'galapagos.deeplink.unknown_screen', {
    route_template: templatizeRumViewName(path.split(/[?#]/, 1)[0] || '/'),
    surface: 'web',
  });
}

/** Fire the canonical page_view through the provider interface (never a direct SDK call). */
export function trackPageView(path: string): void {
  if (REDIRECT_ONLY_WEB_ROUTES.has(pathnameOf(path))) return;
  const routeTemplate = canonicalWebRouteTemplate(path);
  // An unregistered route is logged and dropped, never emitted under a
  // substitute identity — a synthetic screen id would pollute journey funnels.
  if (routeTemplate === null) {
    logUnknownWebRoute(path);
    return;
  }
  const screenId = webScreenId(routeTemplate);
  captureProductEvent(
    getProvider().analytics,
    buildViewEvent({
      screen_id: screenId,
      surface: 'web',
      platform: 'web',
      route_template: routeTemplate,
      environment: normalizeAnalyticsEnvironment(process.env.NEXT_PUBLIC_DATADOG_ENV),
      contract_version: PRODUCT_ANALYTICS_CONTRACT_VERSION,
      ...signupViewProperties(routeTemplate),
    }),
  );
}

/**
 * The legacy signup stamped whether its link-driven incentive reminder showed
 * on the first-call booking view; the rebuilt booking step keeps that flag,
 * read from the same URL the step reads.
 */
function signupViewProperties(routeTemplate: string): { incentive_reminder?: boolean } {
  if (routeTemplate !== '/booking') return {};
  const search = typeof window === 'undefined' ? '' : window.location.search;
  return { incentive_reminder: currentBookingIncentive(search) !== null };
}

export function trackWebAction(
  path: string,
  actionId: string,
  inputMethod: 'keyboard' | 'pointer',
  elementKey?: string,
): void {
  const routeTemplate = canonicalWebRouteTemplate(path);
  // Actions carry a screen identity, so an unregistered route drops the action
  // for the same reason page views do.
  if (routeTemplate === null) {
    logUnknownWebRoute(path);
    return;
  }
  const screenId = webScreenId(routeTemplate);
  captureProductEvent(
    getProvider().analytics,
    buildActionEvent({
      screen_id: screenId,
      action_id: actionId,
      surface: 'web',
      platform: 'web',
      input_method: inputMethod,
      ...(elementKey !== undefined ? { element_key: elementKey } : {}),
    }),
  );
}

export function trackWebActionWhenReady(
  path: string,
  actionId: string,
  inputMethod: 'keyboard' | 'pointer',
  elementKey?: string,
): Promise<void> {
  return analyticsReady().then(
    () => {
      try {
        trackWebAction(path, actionId, inputMethod, elementKey);
      } catch {
        getProvider().logs.log('warn', 'galapagos.analytics.policy_rejected', { surface: 'web' });
      }
    },
    () => {},
  );
}

/**
 * Send a product event built outside a page view or action (a legacy-parity or
 * domain event) once init has settled. The event is built inside the guard, so
 * a policy rejection, a failing build or a failing sink is logged and
 * swallowed, never surfaced.
 *
 * Readiness is read one microtask later. A page's mount effect runs before the
 * root `AnalyticsProvider` effect that starts init, so reading it in the same
 * tick sees the pre-init resolved promise and sends the event to noop.
 */
export function trackProductEventWhenReady(build: () => ProductEvent): Promise<void> {
  return Promise.resolve()
    .then(() => analyticsReady())
    .then(
      () => {
        try {
          captureProductEvent(getProvider().analytics, build());
        } catch {
          getProvider().logs.log('warn', 'galapagos.analytics.policy_rejected', {
            surface: 'web',
          });
        }
      },
      () => {},
    );
}

/** Send a production-named legacy-parity event once init has settled. */
export const trackLegacyParityEventWhenReady = trackProductEventWhenReady;

/**
 * Send one structured log once init has settled, with the same one
 * microtask deferral as {@link trackProductEventWhenReady}: a page's mount
 * effect runs before the root provider starts init, and the provider is noop
 * until then. A failing sink is swallowed.
 */
export function logWhenReady(
  level: 'info' | 'warn',
  key: string,
  attributes: Record<string, string | boolean>,
): Promise<void> {
  return Promise.resolve()
    .then(() => analyticsReady())
    .then(
      () => {
        try {
          getProvider().logs.log(level, key, attributes);
        } catch {
          // Losing one operational log never surfaces to the member.
        }
      },
      () => {},
    );
}

/**
 * Routes that only redirect elsewhere. They are pages, but not screens: they
 * emit no `screen_viewed` and no unknown-screen warning, since the page they
 * hand off to reports the view.
 */
export const REDIRECT_ONLY_WEB_ROUTES: ReadonlySet<string> = new Set(['/signup']);

/**
 * The SAML landing stays at the site root: the identity provider's callback is
 * the exact address `{origin}/saml`.
 */
const SAML_ROUTES = new Set(['/saml']);

/**
 * Web screen ids that stay registered after their pages were removed. The
 * shared screen-id enum is a wire contract (installed builds send these ids as
 * reminder destinations), so the ids outlive the pages; nothing emits them.
 */
export const RETIRED_WEB_SCREEN_IDS: ReadonlySet<string> = new Set([
  'greeting',
  'download',
  'saml-contact',
]);

/** The rebuilt signup flow's pages at the site root. */
const SIGNUP_ROUTES: ReadonlySet<string> = new Set(SIGNUP_PAGE_ROUTE_TEMPLATES);

/**
 * The signup entry is served at `/`, but a product route template needs at
 * least one segment, so the entry keeps the template it had before the flow
 * moved to the root, and with it the `signup` screen id.
 */
const SIGNUP_ENTRY_ROUTE_TEMPLATE = '/signup';

/** The registered route template for a path, or null for a route outside the registry. */
export function canonicalWebRouteTemplate(path: string): string | null {
  const template = templatizeRumViewName(pathnameOf(path));
  if (template === '/component-gallery') return '/component-gallery';
  if (SAML_ROUTES.has(template)) return template;
  if (template === '/') return SIGNUP_ENTRY_ROUTE_TEMPLATE;
  if (SIGNUP_ROUTES.has(template)) return template;
  return null;
}

export function webScreenId(routeTemplate: string): string {
  // The signup pages moved from under `/signup` to the site root and kept their
  // screen ids, so funnels read across the move: `/<id>` is `signup/<id>`.
  if (SIGNUP_ROUTES.has(routeTemplate)) return `signup${routeTemplate}`;
  return routeTemplate
    .replace(/^\//, '')
    .split('/')
    .map((segment) => (segment.startsWith(':') ? 'detail' : segment))
    .join('/');
}

/** Reusable auth seam for future authenticated web routes; raw actor IDs are rejected. */
export function applyWebAnalyticsContext(input: unknown): AnalyticsContext {
  const context = analyticsContextSchema.parse(input);
  const analytics = getProvider().analytics;
  analytics.setUser(context.subject_id, context.person_properties);
  for (const event of buildContextFeatureFlagEvaluationEvents(context)) {
    captureProductEvent(analytics, event);
  }
  return context;
}

export function clearWebAnalyticsIdentity(): void {
  getProvider().analytics.clearUser();
}

/**
 * Fire page_view only once the async provider init has settled — so the first
 * navigation isn't tracked against the noop default and lost. A failed init
 * resolves to a no-op rather than an unhandled rejection.
 */
export function trackPageViewWhenReady(path: string): Promise<void> {
  return analyticsReady().then(
    () => trackPageView(path),
    () => {},
  );
}

/**
 * Start a named Datadog RUM view for the given route (manual view tracking), once
 * init has settled. No-op when RUM is off. Paired with `trackPageViewWhenReady`
 * on every route change.
 */
export function trackRumViewWhenReady(path: string): Promise<void> {
  return analyticsReady().then(
    () => startWebRumView(path),
    () => {},
  );
}
